import {
  isClientOnALaCartePlan,
  type MarketedMembershipPlan,
  type MarketedMembershipPlanType,
} from '@frontend/api-client';
import type { Card } from '@frontend/api-types';
import { formatCurrencyNatural, formatNumber, pluralize } from '@frontend/duck-tape';
import { useSetTimeout } from '@frontend/react';
import type { BuyCreditsState, BuyCreditStep, SubscriptionPlan } from '@frontend/react-domain';
import { buyCreditsStateMachine } from '@frontend/react-domain';
import { Icon, Modal, showToast, Text, XStack, YStack } from '@frontend/web-react';
import { CalloutBadge } from '@frontend/web-react/components/Primitives/Badges';
import { usePricingTerminology } from '@frontend/web/context';
import { getValidatedPromoInput, useMembershipPlans } from '@frontend/web/hooks';
import {
  useBuyCreditsMutation,
  useListCardsQuery,
  useRetrieveClientQuery,
  useUpdatePlanMutation,
} from '@frontend/web/hooks/api';
import { handleMutation } from '@frontend/web/utils';
import { Divider } from '@mantine/core';
import { useCallback, useMemo, useReducer } from 'react';
import { CreditPaymentLoading } from './CreditPaymentLoading';
import { SelectCredits } from './SelectCredits';
import { SelectPayment } from './SelectPayment';
import { SelectSubscriptionPlan } from './SelectSubscriptionPlan';

export type BuyCreditsModalProps = {
  changeSubscriptionTitle?: string;
  closeModal: () => void;
  initialState?: BuyCreditsState;
  isOpen: boolean;
  minimumCredits?: number;
  onCreditsPurchased?: () => void;
  stepTitleLookupOverrides?: Partial<Record<BuyCreditStep, (state: BuyCreditsState) => string>>;
  updateMembershipTitle?: string;
};

export const BuyCreditsModal = ({
  changeSubscriptionTitle = 'Subscribe and Save',
  closeModal,
  initialState = { step: 'selectPackage' },
  isOpen,
  minimumCredits,
  onCreditsPurchased,
  stepTitleLookupOverrides,
  updateMembershipTitle = 'Update membership',
}: BuyCreditsModalProps) => {
  const [{ canGoBack, card, package: pkg, step, subscriptionPlan }, dispatch] = useReducer(
    buyCreditsStateMachine,
    initialState,
  );
  const { data: client, refetch: refetchClient } = useRetrieveClientQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const validatedPromo = getValidatedPromoInput({
    promoCode: subscriptionPlan?.promotion?.promoCode,
    promotionId: subscriptionPlan?.promotion?.promotionDetails.promotionId,
  });
  const { displayPlanLookup, displaySelectablePlans } = useMembershipPlans({ validatedPromo });

  const defaultTitleLookup: Partial<Record<BuyCreditStep, (state: BuyCreditsState) => string>> = useMemo(
    () => ({
      changeSubscription: () => changeSubscriptionTitle,
      selectCard: (state) => (state.subscriptionPlan ? updateMembershipTitle : pricingTerminology.add_button),
      selectPackage: () => pricingTerminology.balance_title,
    }),
    [changeSubscriptionTitle, pricingTerminology, updateMembershipTitle],
  );
  const titleLookup = { ...defaultTitleLookup, ...stepTitleLookupOverrides };
  const [buyCreditsMutation, { isLoading: isBuying }] = useBuyCreditsMutation();
  const [updatePlanMutation] = useUpdatePlanMutation();
  const { refetch: refetchCards } = useListCardsQuery();

  const handleSelectCard = useCallback(
    async (card: Card) => {
      if ((!pkg && !subscriptionPlan) || isBuying) {
        return;
      }

      if (pkg) {
        dispatch({ card, package: pkg, type: 'pay' });
        await handleMutation({
          mutation: () =>
            buyCreditsMutation({
              amountOfCredits: pkg.amount,
              cardId: card.id,
              totalPrice: pkg.priceCent,
            }).unwrap(),
          onError: () => {
            dispatch({ card, package: pkg, type: 'failPayment' });
          },
          onSuccess: () => {
            dispatch({ card, package: pkg, type: 'completePayment' });
            onCreditsPurchased?.();
          },
          showSuccessToast: false,
        });
      } else if (subscriptionPlan) {
        const planData = displayPlanLookup[subscriptionPlan.planType];
        if (!planData) return;
        dispatch({ card, subscriptionPlan, type: 'pay' });
        await handleMutation({
          mutation: () =>
            updatePlanMutation({
              plan: planData.planCode,
              promotionCode: subscriptionPlan.promotion?.promoCode,
            }).unwrap(),
          onError: () => {
            dispatch({ card, subscriptionPlan, type: 'failPayment' });
          },
          onSuccess: async () => {
            dispatch({ card, subscriptionPlan, type: 'completePayment' });
            await refetchClient();
            onCreditsPurchased?.();
          },
          showSuccessToast: false,
        });
      }
    },
    [
      buyCreditsMutation,
      dispatch,
      isBuying,
      onCreditsPurchased,
      pkg,
      displayPlanLookup,
      refetchClient,
      subscriptionPlan,
      updatePlanMutation,
    ],
  );

  const handleApplePayComplete = useCallback(async () => {
    const { data: updatedCards } = await refetchCards();
    const defaultCard = updatedCards?.find((card) => card.isDefault);
    if (defaultCard) {
      await handleSelectCard(defaultCard);
    } else {
      showToast({
        message: 'Payment method saved, but your purchase could not be completed. Please try again.',
        title: 'Purchase incomplete',
        variant: 'danger',
      });
    }
  }, [handleSelectCard, refetchCards]);

  const minSubscriptionPriceAvailable = useMemo(() => {
    const canUpgrade = isClientOnALaCartePlan(client);
    if (!canUpgrade || displaySelectablePlans.length === 0) {
      return undefined;
    }
    return Math.min(...displaySelectablePlans.map((plan) => plan.price)) as Parameters<typeof formatCurrencyNatural>[0];
  }, [displaySelectablePlans, client]);

  const successMessage = useMemo(() => {
    if (pkg) {
      return showCredits
        ? `You successfully purchased ${formatNumber(pkg.amount)} ${pluralize(pricingTerminology.unit_term, pkg.amount)}.`
        : 'Your account has been updated.';
    }
    if (subscriptionPlan) {
      return `You successfully upgraded to ${displayPlanLookup[subscriptionPlan.planType]?.label ?? 'your new plan'}.`;
    }
    return '';
  }, [pkg, showCredits, pricingTerminology.unit_term, subscriptionPlan, displayPlanLookup]);

  const handleSelectUpgradeOffer = useCallback(() => {
    dispatch({ type: 'startSubscriptionChange' });
  }, [dispatch]);

  const handleContinueWithPlan = useCallback(
    (planType: MarketedMembershipPlanType, promotion?: SubscriptionPlan['promotion']) => {
      dispatch({
        subscriptionPlan: {
          planType,
          promotion,
        },
        type: 'continueWithPlanSelection',
      });
    },
    [dispatch],
  );

  const resetFormAfterDelay = useSetTimeout(
    () => {
      dispatch({ type: 'reset' });
    },
    300,
    { autoStart: false },
  );

  const closeAndReset = () => {
    closeModal();
    resetFormAfterDelay();
  };

  return (
    <Modal
      Header={
        titleLookup[step] ? (
          <YStack>
            <XStack alignItems="center" justifyContent="between" p="md">
              <XStack alignItems="center" className="size-xl" justifyContent="start">
                {canGoBack ? (
                  <Icon
                    accessibilityLabel="Go back"
                    canFocus
                    enableBackgroundColor={false}
                    name="IconArrowLeft"
                    onClick={() => dispatch({ type: 'back' })}
                    size={24}
                  />
                ) : null}
              </XStack>

              <Text color="secondary" type="h5">
                {titleLookup[step]?.({ canGoBack, card, package: pkg, step, subscriptionPlan })}
              </Text>

              <XStack alignItems="center" className="size-xl" justifyContent="end">
                <Icon
                  accessibilityLabel="Close modal"
                  canFocus
                  enableBackgroundColor={false}
                  name="IconX"
                  onClick={closeAndReset}
                  size={24}
                />
              </XStack>
            </XStack>
            <Divider />
          </YStack>
        ) : null
      }
      closeOnClickOutside={step !== 'processing'}
      closeOnEscape={step !== 'processing'}
      isOpen={isOpen}
      onClose={() => {
        if (step !== 'processing') {
          closeAndReset();
        }
      }}
      padding="none"
      size={'xl'}
    >
      {step === 'selectPackage' ? (
        <SelectCredits
          minimumCredits={minimumCredits}
          onSelectCreditPackage={(pkg) => {
            dispatch({ package: pkg, type: 'selectPackage' });
          }}
          selectedCreditPackage={pkg}
          upgradeOffer={
            minSubscriptionPriceAvailable !== undefined
              ? {
                  commentary: 'Pick a subscription to get discounted credits monthly',
                  onPress: handleSelectUpgradeOffer,
                  ornament: <CalloutBadge>MOST POPULAR</CalloutBadge>,
                  subtitle: `from ${formatCurrencyNatural(minSubscriptionPriceAvailable)}`,
                  title: 'Subscribe and Save',
                }
              : undefined
          }
        />
      ) : step === 'changeSubscription' ? (
        <SelectSubscriptionPlan onContinue={handleContinueWithPlan} subscriptionPlan={subscriptionPlan} />
      ) : step === 'selectCard' && (pkg || subscriptionPlan) ? (
        <SelectPayment
          onApplePayComplete={handleApplePayComplete}
          onSelectCard={handleSelectCard}
          promoCode={subscriptionPlan?.promotion?.promoCode}
          promotionDescription={subscriptionPlan?.promotion?.promotionDescription}
          purchasePackage={
            pkg
              ? { package: pkg, type: 'credits' }
              : { planType: subscriptionPlan!.planType as MarketedMembershipPlanType, type: 'subscription' }
          }
          selectedPlanData={
            subscriptionPlan
              ? (displayPlanLookup[subscriptionPlan.planType] as MarketedMembershipPlan | undefined)
              : undefined
          }
        />
      ) : step === 'processing' && card ? (
        <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
          <CreditPaymentLoading />
          <Text type="h5">{`We're processing your payment`}</Text>
          <Text type="p-sm">{`Don't close this window.`}</Text>
        </YStack>
      ) : step === 'success' ? (
        <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
          <YStack bg="green200" p="sm" radius="full">
            <Icon className="hover:cursor-default" color="green" name="IconCheck" size="xl" />
          </YStack>
          <Text type="h5">Payment successful!</Text>
          <Text type="p-sm">{successMessage}</Text>
        </YStack>
      ) : (
        <YStack alignItems="center" gap="sm" justifyContent="center" px="xl" py="xl">
          <YStack bg="red200" p="sm" radius="full">
            <Icon color="danger" name="IconAlertCircle" size="xl" />
          </YStack>
          <Text type="h5">Payment unsuccessful</Text>
          <Text type="p-sm">There was an unexpected error - please try again.</Text>
        </YStack>
      )}
    </Modal>
  );
};
